<template>
    <div class="mt-3">
        <div class="flex flex-col">
            <div class="flex flex-wrap gap-2 justify-center">
                <button
                    v-for="option in quickOptions"
                    :key="option.key"
                    :class="[
                        'rounded-lg border-brand-blue border-2 py-1 px-2 text-sm transition-colors min-w-16',
                        selectedKey === option.key
                            ? 'bg-brand-blue text-white'
                            : ' text-theme-primary hover:border-stack-primary hover:text-stack-primary'
                    ]"
                    @click="selectQuickOption(option)"
                >
                    <small>{{ option.label }}</small>
                </button>
            </div>

            <div class="mt-3">
                <div
                    class="text-center text-sm font-medium text-theme-base mb-2"
                >
                    O personaliza el rango
                </div>
                <div class="flex flex-wrap gap-2 justify-center">
                    <div class="flex items-center">
                        <custom-input
                            v-model.number="customValue"
                            type="number"
                            min="1"
                            class="w-32"
                            placeholder="Cantidad"
                            @input="handleCustomChange"
                        />
                    </div>

                    <div class="flex items-center">
                        <custom-simple-select
                            v-model="customUnit"
                            class="w-32"
                            :options="customUnitOptions"
                            @change="handleCustomChange"
                        />
                    </div>
                </div>
                <div
                    v-if="allowFutureDates"
                    class="flex items-center justify-center mt-1"
                >
                    <div
                        class="flex items-center gap-4 rounded-md px-3 py-1 border border-theme-border"
                    >
                        <label class="inline-flex items-center cursor-pointer">
                            <input
                                v-model="customDirection"
                                type="radio"
                                name="direction"
                                value="past"
                                class="form-radio text-theme-primary mr-2"
                                @change="handleCustomChange"
                            />
                            <span class="text-sm">Pasado</span>
                        </label>
                        <label class="inline-flex items-center cursor-pointer">
                            <input
                                v-model="customDirection"
                                type="radio"
                                name="direction"
                                value="future"
                                class="form-radio text-theme-primary mr-2"
                                @change="handleCustomChange"
                            />
                            <span class="text-sm">Futuro</span>
                        </label>
                    </div>
                </div>
            </div>

            <div
                class="rounded-lg border py-2 px-3 border-theme-border text-center mt-3"
            >
                <div class="text-xs text-theme-muted mb-1">
                    Rango seleccionado:
                </div>
                <div class="text-sm font-semibold text-theme-primary">
                    {{ formatDateRange(calculatedRange.begin) }} -
                    {{ formatDateRange(calculatedRange.end) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTime } from "luxon";

import CustomInput from "@/components/utils/CustomInput.vue";
import CustomSimpleSelect from "@/components/utils/CustomSimpleSelect.vue";

export default {
    name: "RelativeDatePicker",
    components: { CustomInput, CustomSimpleSelect },
    model: { prop: "value", event: "change" },
    props: {
        value: { type: Object, default: () => ({}) },
        allowFutureDates: { type: Boolean, default: false }
    },
    data() {
        return {
            customValue: 1,
            customUnit: "days",
            customDirection: "past", // 'past' o 'future'
            selectedKey: "-7days", // Key inicial por defecto
            customUnitOptions: [
                { label: "Días", value: "days" },
                { label: "Semanas", value: "weeks" },
                { label: "Meses", value: "months" }
            ]
        };
    },
    computed: {
        quickOptions() {
            return this.allowFutureDates
                ? [
                      { label: "Hoy", value: 0, unit: "days", key: "today" },
                      {
                          label: "Mañana",
                          value: 1,
                          unit: "days",
                          key: "+1days"
                      },
                      { label: "Ayer", value: 1, unit: "days", key: "-1days" },
                      {
                          label: "Próximos 7 días",
                          value: 7,
                          unit: "days",
                          key: "+7days"
                      },
                      {
                          label: "Próximos 30 días",
                          value: 30,
                          unit: "days",
                          key: "+30days"
                      },
                      {
                          label: "Próximo mes",
                          value: 1,
                          unit: "months",
                          key: "+1months"
                      },
                      {
                          label: "Últimos 7 días",
                          value: 7,
                          unit: "days",
                          key: "-7days"
                      },
                      {
                          label: "Últimos 30 días",
                          value: 30,
                          unit: "days",
                          key: "-30days"
                      },
                      {
                          label: "Último mes",
                          value: 1,
                          unit: "months",
                          key: "-1months"
                      }
                  ]
                : [
                      { label: "Hoy", value: 0, unit: "days", key: "today" },
                      { label: "Ayer", value: 1, unit: "days", key: "-1days" },
                      {
                          label: "Últimos 7 días",
                          value: 7,
                          unit: "days",
                          key: "-7days"
                      },
                      {
                          label: "Últimos 30 días",
                          value: 30,
                          unit: "days",
                          key: "-30days"
                      },
                      {
                          label: "Último mes",
                          value: 1,
                          unit: "months",
                          key: "-1months"
                      }
                  ];
        },
        calculatedRange() {
            const now = DateTime.now().startOf("day");
            let begin, end;

            const isFuture = this.selectedKey.startsWith("+");
            const isToday = this.selectedKey === "today";

            const option = this.quickOptions.find(
                (o) => o.key === this.selectedKey
            ) || { value: this.customValue, unit: this.customUnit };

            if (isToday) {
                begin = now;
                end = now;
            } else if (isFuture) {
                begin = now;
                end = now.plus({ [option.unit]: option.value });
            } else {
                end = now;
                begin = now.minus({ [option.unit]: option.value });
            }

            return {
                begin: begin.toISODate(),
                end: end.toISODate()
            };
        }
    },
    watch: {
        calculatedRange: {
            handler(val) {
                this.$emit("change", val);
            },
            immediate: true
        }
    },
    methods: {
        selectQuickOption(option) {
            this.selectedKey = option.key;
            this.customValue = option.value;
            this.customUnit = option.unit;
            this.customDirection = option.key.startsWith("+")
                ? "future"
                : "past";
        },
        handleCustomChange() {
            const prefix = this.customDirection === "future" ? "+" : "-";
            this.selectedKey = `${prefix}${this.customValue}${this.customUnit}`;
        },
        formatDateRange(dateStr) {
            if (!dateStr) return "";
            return DateTime.fromISO(dateStr).toFormat("dd/MM/yyyy");
        }
    }
};
</script>
